import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Brief, GscInsightsResponse, GscOpportunity } from '@seo/types';
import { apiGet, apiPost } from '@/lib/api';

export function useGscInsights(projectId: string | undefined) {
  return useQuery({
    queryKey: ['gsc-insights', projectId],
    queryFn: () =>
      apiGet<GscInsightsResponse>(`/v1/projects/${projectId}/gsc/insights`),
    enabled: !!projectId,
    staleTime: 5 * 60_000,
  });
}

/**
 * Look up a GSC opportunity row matching a specific tracked keyword.
 *
 * H3 (Surface GSC vs SERP): the GSC insights endpoint only returns the
 * top opportunities (queries with high impressions past position 10), not
 * per-keyword series. So this is best-effort: we match by case-insensitive
 * exact query, and callers must handle `undefined` gracefully.
 *
 * TODO(H3-gsc): if we ever need average position for *any* tracked
 * keyword (not just opportunities), we'll need a per-keyword GSC endpoint
 * — not adding one here per plan doc.
 */
export function useGscInsightsForKeyword(
  projectId: string | undefined,
  keyword: string | undefined,
): { opportunity: GscOpportunity | undefined; loaded: boolean } {
  const { data } = useGscInsights(projectId);
  if (!data || !keyword) {
    return { opportunity: undefined, loaded: !!data };
  }
  const needle = keyword.trim().toLowerCase();
  const opportunity = data.opportunities.find(
    (o) => o.query.trim().toLowerCase() === needle,
  );
  return { opportunity, loaded: true };
}

export function useCreateGscRefreshBrief(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { page_url: string; target_keyword?: string }) =>
      apiPost<Brief>(`/v1/projects/${projectId}/gsc/refresh-brief`, {
        ...body,
        delivery: 'markdown',
      }),
    onSuccess: (brief) => {
      void qc.invalidateQueries({ queryKey: ['briefs'] });
      qc.setQueryData(['brief', brief.id], brief);
    },
  });
}
